/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-04
 */

// Add third-party dependencies.
import { parseISO } from 'date-fns';
import { formatLongDate } from './dateLocalization';

// Add local dependencies.
import type {
  ChangelogData,
  ChangelogEntry,
  ChangelogChange,
  ChangeTypeConfig,
  ChangelogYearGroup,
} from '../types/changelog';

/**
 * Configuration for different change types with their display properties.
 */
export const CHANGE_TYPE_CONFIG: Record<string, ChangeTypeConfig> = {
  feature: {
    label: 'New Features',
    icon: 'StarIcon',
    color: 'primary',
  },
  improvement: {
    label: 'Improvements',
    icon: 'TrendingUpIcon',
    color: 'success',
  },
  bugfix: {
    label: 'Bug Fixes',
    icon: 'BugReportIcon',
    color: 'info',
  },
  breaking: {
    label: 'Breaking Changes',
    icon: 'WarningIcon',
    color: 'error',
  },
};

/**
 * Parse a version string into comparable parts.
 * @param version - Version string (e.g., "1.2.3")
 * @returns Object with major, minor, patch numbers
 */
const parseVersion = (
  version: string
): { major: number; minor: number; patch: number } => {
  // Remove 'v' prefix if present
  const cleanVersion = version.replace(/^v/, '');
  const parts = cleanVersion.split('.').map(Number);

  return {
    major: parts[0] || 0,
    minor: parts[1] || 0,
    patch: parts[2] || 0,
  };
};

/**
 * Compare two version objects.
 * @param a - First version object
 * @param b - Second version object
 * @returns -1 if a < b, 1 if a > b, 0 if equal
 */
const compareVersions = (
  a: { major: number; minor: number; patch: number },
  b: { major: number; minor: number; patch: number }
): number => {
  if (a.major !== b.major) return a.major - b.major;
  if (a.minor !== b.minor) return a.minor - b.minor;
  return a.patch - b.patch;
};

/**
 * Load changelog data from static JSON file.
 * @returns Promise resolving to changelog data
 */
export const getChangelogData = async (): Promise<ChangelogData> => {
  try {
    const response = await fetch('/data/changelog.json');
    if (!response.ok) {
      throw new Error(`Failed to load changelog: ${response.status}`);
    }
    const data: ChangelogData = await response.json();

    // Sort entries by version (newest first) - create new sorted array
    const sortedEntries = [...data.entries].sort((a, b) => {
      const versionA = parseVersion(a.version);
      const versionB = parseVersion(b.version);
      return compareVersions(versionB, versionA);
    });

    return {
      ...data,
      entries: sortedEntries,
    };
  } catch (error) {
    console.error('Error loading changelog data:', error);
    // Return empty changelog on error
    return {
      entries: [],
      meta: {
        lastUpdated: new Date().toISOString(),
        totalEntries: 0,
      },
    };
  }
};

/**
 * Format a date string for display in changelog.
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
export const formatChangelogDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    // Note: formatLongDate uses 'PPP' format which produces locale-aware long dates
    // For changelog, we want English-only format, so we pass 'en' explicitly
    return formatLongDate(date, 'en');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Unknown date';
  }
};

/**
 * Group changes by their type for organized display.
 * @param changes - Array of changelog changes
 * @returns Object with changes grouped by type
 */
export const groupChangesByType = (
  changes: ChangelogChange[]
): Record<string, ChangelogChange[]> =>
  changes.reduce(
    (groups, change) => {
      const { type } = change;
      const newGroups = { ...groups };
      if (!newGroups[type]) {
        newGroups[type] = [];
      }
      newGroups[type] = [...(newGroups[type] || []), change];
      return newGroups;
    },
    {} as Record<string, ChangelogChange[]>
  );

/**
 * Get the display order for change types.
 * @returns Array of change types in display order
 */
export const getChangeTypeOrder = (): string[] => [
  'breaking',
  'feature',
  'improvement',
  'bugfix',
];

/**
 * Filter changelog entries to only show published ones.
 * @param entries - Array of changelog entries
 * @returns Filtered array of published entries
 */
export const getPublishedEntries = (
  entries: ChangelogEntry[]
): ChangelogEntry[] =>
  entries.filter(entry => entry.isPublished && !entry.isDraft);

/**
 * Generate a changelog entry from git commits (placeholder for future automation).
 * @param version - Version string
 * @param commits - Array of commit messages
 * @returns Generated changelog entry
 */
export const generateChangelogFromCommits = (
  version: string,
  commits: string[]
): ChangelogEntry =>
  // This is a placeholder for future automation
  // For now, return a basic template
  ({
    version,
    date: new Date().toISOString(),
    title: `Version ${version}`,
    description: 'Automated changelog entry - please edit',
    changes: commits.map(commit => ({
      type: 'improvement',
      message: commit,
    })),
    isPublished: false,
    isDraft: true,
  });

/**
 * Get the latest published version from changelog entries.
 * @param entries - Array of changelog entries
 * @returns Latest version string or null if no published entries
 */
export const getLatestVersion = (entries: ChangelogEntry[]): string | null => {
  const publishedEntries = getPublishedEntries(entries);
  if (publishedEntries.length === 0) return null;

  return publishedEntries[0].version;
};

/**
 * Group entries by calendar year for the collapsible /changelog display.
 * Entries are expected pre-sorted newest-first; the resulting groups are
 * ordered newest year first and preserve the incoming entry order within
 * each year. The year is taken from the ISO date string directly (not via
 * Date parsing) so display grouping never shifts across a timezone boundary.
 *
 * @param entries - Array of changelog entries (newest first)
 * @returns Year groups, newest year first
 */
export const groupEntriesByYear = (
  entries: ChangelogEntry[]
): ChangelogYearGroup[] => {
  const byYear: Record<string, ChangelogEntry[]> = {};

  entries.forEach(entry => {
    const year = entry.date.slice(0, 4);
    if (!byYear[year]) {
      byYear[year] = [];
    }
    byYear[year].push(entry);
  });

  return Object.keys(byYear)
    .sort((a, b) => Number(b) - Number(a))
    .map(year => ({ year, entries: byYear[year] }));
};
